// Team-member store for the Collaboration page. Talks to the collaboration
// API (Cognito-backed invites) when it's available, and falls back to
// localStorage for plain local dev so the UI stays functional offline.
import { getAccessToken, getIdToken } from './cognito.js'

const STORAGE_KEY = 'revailosales.team'

// The collaboration Lambda requires the caller's Cognito ID token (its
// _require_member / _require_admin gates), and it lives behind the same gateway
// as every other endpoint. team.js used to send NEITHER a token NOR a fallback
// base — so on localhost `/api/collaboration` hit the dev history-fallback
// (HTML, not the API) and the roster silently fell back to an empty localStorage
// list ("No teammates yet"), while every sibling module (data.jsx, hubspot.js,
// chat.js) already did both and worked. This mirrors them, so
// the roster loads identically on localhost and on saassaleshq.com.
const API_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

async function authHeaders(extra = {}) {
  const token = await getIdToken()
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

// Same-origin `/api/collaboration` first (production via CloudFront, or dev with
// API_PROXY set); when that isn't really the API — the dev server answers
// /api/* with index.html — retry the deployed gateway. The Cognito token rides
// along on both, so this changes WHERE the request goes, never WHO may ask.
async function collabFetch(options = {}) {
  const headers = await authHeaders(options.headers || {})
  let res = await fetch('/api/collaboration', { ...options, headers })
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    res = await fetch(`${API_FALLBACK_BASE}/collaboration`, { ...options, headers })
  }
  return res
}

// Older builds seeded a fabricated owner record (invited_at 2026-06-01) into
// localStorage; purge it on read so the roster only ever contains people who
// were actually invited. Real rosters come from the collaboration API.
function purgeSeed(members) {
  if (!Array.isArray(members)) return []
  return members.filter(
    (m) => !(m.email === 'jackson@dentalsitemaster.com' && m.invited_at === '2026-06-01'),
  )
}

function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    // Callers map/filter this, so a non-array in storage would throw during a
    // render. Treat any other shape as empty.
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return purgeSeed(parsed)
    }
  } catch {
    /* ignore */
  }
  return []
}

function writeLocal(members) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(members))
  return members
}

async function apiJson(res) {
  if (!res.ok) return null
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) return null
  return res.json()
}

export async function listMembers() {
  try {
    const data = await apiJson(await collabFetch())
    if (data) return { members: data.members || [], mode: 'api' }
  } catch {
    /* fall back to local */
  }
  return { members: readLocal(), mode: 'local' }
}

export async function inviteMember({ email, name, role, permissions, message = '' }) {
  const member = {
    email: email.trim().toLowerCase(),
    name: name.trim(),
    role,
    // Custom checkbox grants from the invite wizard; omitted → role preset.
    ...(Array.isArray(permissions) ? { permissions } : {}),
    status: 'Invited',
    invited_at: new Date().toISOString().slice(0, 10),
  }
  try {
    const res = await collabFetch({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: member.email,
        name: member.name,
        role,
        ...(Array.isArray(permissions) ? { permissions } : {}),
        message,
      }),
    })
    if (res.status === 409) throw new Error('That email has already been invited.')
    const data = await apiJson(res)
    if (data && data.member) return { member: data.member, mode: 'api' }
  } catch (err) {
    if (err.message.includes('already been invited')) throw err
    // network / non-JSON → fall back to local
  }
  const members = readLocal()
  if (members.some((m) => m.email === member.email)) {
    throw new Error('That email has already been invited.')
  }
  writeLocal([...members, member])
  return { member, mode: 'local' }
}

// Change an existing member's access (RBAC). `permissions` is an array of
// permission keys to pin custom grants, or null to clear overrides back to the
// role preset; leave it undefined to touch only the role. API-first, local
// fallback.
export async function updateMember(email, { role, permissions } = {}) {
  const clean = (email || '').trim().toLowerCase()
  const patch = { email: clean }
  if (role !== undefined) patch.role = role
  if (permissions !== undefined) patch.permissions = permissions
  // A JSON error from the API is a real rejection (member gone, bad payload) —
  // surface it. Only network failures / non-JSON responses mean "offline" and
  // fall back to the local roster.
  let rejection = null
  try {
    const res = await collabFetch({
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if ((res.headers.get('content-type') || '').includes('application/json')) {
      const data = await res.json().catch(() => null)
      if (data && res.ok && data.member) return { member: data.member, mode: 'api' }
      if (data && data.error) rejection = new Error(data.error)
    }
  } catch {
    /* network failure → fall back to local */
  }
  if (rejection) throw rejection
  const members = readLocal().map((m) => {
    if (m.email !== clean) return m
    const next = { ...m }
    if (role !== undefined) next.role = role
    if (permissions !== undefined) {
      if (permissions === null) delete next.permissions
      else next.permissions = permissions
    }
    return next
  })
  writeLocal(members)
  return { member: members.find((m) => m.email === clean) || null, mode: 'local' }
}

export const updateMemberRole = (email, role) => updateMember(email, { role })

// An admin sets a member's password; the lambda makes it permanent in Cognito
// and emails it to the member in plain text. -> { emailed, emailError }.
// API only, no local fallback: a password that only "changed" in this browser
// would be a lie, so anything but a real answer from the lambda throws.
export async function setMemberPassword(email, password) {
  const res = await collabFetch({
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'set_password', email: (email || '').trim().toLowerCase(), password }),
  })
  const data = (res.headers.get('content-type') || '').includes('application/json')
    ? await res.json().catch(() => null)
    : null
  if (!res.ok || !data) throw new Error((data && data.error) || 'The password could not be changed — the team API did not answer.')
  // An older lambda reads this as a role change and refuses it for having no
  // role; anything without `changed` is that, never a success.
  if (!data.changed) throw new Error('The password could not be changed — the team API is running an older build.')
  return { emailed: Boolean(data.emailed), emailError: data.email_error || '' }
}

// Change YOUR OWN password. Cognito checks the current one (ChangePassword on
// your own access token), and the lambda emails the new one to you in plain
// text. -> { emailed, emailError }. API only, like setMemberPassword.
export async function changeOwnPassword(currentPassword, newPassword) {
  const accessToken = await getAccessToken()
  if (!accessToken) throw new Error('Your session expired — sign in again, then change your password.')
  const res = await collabFetch({
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'change_own_password',
      current_password: currentPassword,
      password: newPassword,
      access_token: accessToken,
    }),
  })
  const data = (res.headers.get('content-type') || '').includes('application/json')
    ? await res.json().catch(() => null)
    : null
  if (!res.ok || !data) throw new Error((data && data.error) || 'Your password could not be changed — the team API did not answer.')
  if (!data.changed) throw new Error('Your password could not be changed — the team API is running an older build.')
  return { emailed: Boolean(data.emailed), emailError: data.email_error || '' }
}

export async function removeMember(email) {
  try {
    // The collaboration route is /api/collaboration (no path param); the lambda
    // reads the email from the DELETE body.
    const res = await collabFetch({
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (res.ok && (res.headers.get('content-type') || '').includes('application/json')) {
      return { mode: 'api' }
    }
  } catch {
    /* fall back to local */
  }
  writeLocal(readLocal().filter((m) => m.email !== email))
  return { mode: 'local' }
}

// Role keys live in lib/rbac.js (owner/admin/manager/rep/viewer). This legacy
// label list feeds older call-sites; new UI should use ROLE_DEFS from rbac.js.
export const ROLES = ['Admin', 'Manager', 'Sales Rep', 'Viewer']
